import { Route, BrowserRouter as Router, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import { Callback } from "./pages/login/Callback";
import LoginPage from "./pages/login/Login";
import {
  AuthProvider,
  CrossTabLogoutSync,
  LogoutHandler,
  LoggedOutPage,
} from "@hoteltradergit/auth";
import { AnalyticsHubProvider } from "./contexts/AnalyticsHubContext";
import { store, RESET_STORE_ACTION } from "./store";
import RootLayout from "./pages/Root";
import RequireAuth from "./route-guards/RequireAuth";
import Promotions from "./pages/promotions/Promotions";
import Ari from "./pages/ari/Ari";
import ResReport from "./pages/res-report/resReport";
import Unauthorized from "./pages/unauthorized/Unauthorized";
import PriceCheck from "./pages/price-check/PriceCheck";
import Performance from "./pages/performance/Performance";
import UserActivity from "./pages/user-activity/UserActivity";
import {
  RequirePermission,
  RequireUserManagement,
  RequireInternalUser,
  HomeRedirect,
  P,
} from "./rbac";
// Account routes are served by @hoteltradergit/user-management. The provider is
// mounted in src/pages/Root.js via <UserManagementBridge>; the seven config
// fields are assembled in src/userManagementConfig.js.
import {
  AccountLayout,
  MyProfile,
  ManageUsers,
  AddEditUser,
} from "@hoteltradergit/user-management";
import Summary from "./pages/analytics-hub/Summary";
import AlignmentScore from "./pages/analytics-hub/AlignmentScore";
import AriAudit from "./pages/analytics-hub/AriAudit";
import TravelBuyer from "./pages/analytics-hub/TravelBuyer";
import AnalyticsHubPage from "./pages/analytics-hub/AnalyticsHubPage";
import Information from "./pages/property-config/information";
import RoomTypes from "./pages/property-config/room-types";
import RatePlans from "./pages/property-config/rate-plans-derived-rate";
import Policies from "./pages/property-config/policies";
import TaxesFees from "./pages/property-config/taxes-fees";
import ContactUs from "./pages/contact-us/ContactUs";

import "react-toastify/dist/ReactToastify.css";
import "./App.css";

function App() {
  return (
    <AuthProvider
      config={{
        appId: import.meta.env.VITE_PUBLIC_APP_ID || "supplier_portal",
        clientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
        cognitoDomain: import.meta.env.VITE_COGNITO_DOMAIN,
      }}
      // Reset Redux before the lib's logout redirect so user-scoped state
      // (selected property, rate plans) cannot flash for the next session.
      // Covers explicit logout and the interceptor's 401 → /logout path alike.
      onLogout={() => store.dispatch({ type: RESET_STORE_ACTION })}
    >
      <AnalyticsHubProvider>
        <CrossTabLogoutSync />
        <Router>
          <Routes>
            <Route path="/auth" element={<LoginPage />} />
            <Route path="/logout" element={<LogoutHandler />} />
            <Route path="/logged-out" element={<LoggedOutPage />} />
            <Route path="/auth/callback" element={<Callback />} />
            <Route path="/" element={<RequireAuth />}>
              <Route path="/" element={<RootLayout />}>
                {/* Home — falls back to first accessible sidenav item */}
                <Route element={<HomeRedirect />}>
                  <Route index element={<Performance />} />
                </Route>

                {/* Simple page routes */}
                <Route
                  element={<RequirePermission permission={P.Promotions.View} />}
                >
                  <Route path="/promotions" element={<Promotions />} />
                </Route>
                <Route element={<RequirePermission permission={P.Ari.View} />}>
                  <Route path="/ari" element={<Ari />} />
                </Route>
                <Route
                  element={
                    <RequirePermission permission={P.Reservations.View} />
                  }
                >
                  <Route path="/reservations" element={<ResReport />} />
                </Route>
                <Route
                  element={
                    <RequirePermission permission={P.PriceCheck.Search} />
                  }
                >
                  <Route path="/price-check" element={<PriceCheck />} />
                </Route>
                {/* Guarded like every other feature route. The SideNav link alone is not enough:
                    without this, any authenticated user reaches the internal-ops activity log by
                    typing the URL, and it fires the UAL search on mount. */}
                <Route
                  element={
                    <RequirePermission permission={P.UserActivity.View} />
                  }
                >
                  {/* Internal users only, nested so the permission check above stays
                      untouched. Permissions are independent of user type, so this mirrors
                      the SideNav's isInternalUser() check for a hand-typed URL. */}
                  <Route element={<RequireInternalUser />}>
                    <Route path="/user-activity" element={<UserActivity />} />
                  </Route>
                </Route>

                {/* Account section */}
                <Route path="/account" element={<AccountLayout />}>
                  <Route
                    element={<RequirePermission permission={P.Account.View} />}
                  >
                    <Route path="profile" element={<MyProfile />} />
                  </Route>
                  <Route
                    element={<RequirePermission permission={P.Team.View} />}
                  >
                    <Route path="manage-users" element={<ManageUsers />} />
                    {/* Viewing the team list is not enough to add or edit a
                        user — the form needs its own gate. */}
                    <Route element={<RequireUserManagement />}>
                      <Route path="add-user" element={<AddEditUser />} />
                    </Route>
                  </Route>
                </Route>

                {/* Analytics Hub */}
                <Route
                  element={
                    <RequirePermission permission={P.AnalyticsHub.All} />
                  }
                >
                  <Route path="/analytics-hub/summary" element={<Summary />} />
                  <Route
                    path="/analytics-hub/alignment-score"
                    element={<AlignmentScore />}
                  />
                  <Route
                    path="/analytics-hub/ari-audit"
                    element={<AriAudit />}
                  />
                  <Route
                    path="/analytics-hub/travel-buyer"
                    element={<TravelBuyer />}
                  />
                  <Route
                    path="/analytics-hub/:pageName"
                    element={<AnalyticsHubPage />}
                  />
                </Route>

                <Route path="/unauthorized" element={<Unauthorized />} />
                {/* Property Profile routes */}
                <Route
                  element={
                    <RequirePermission permission={P.PropertyConfig.View} />
                  }
                >
                  <Route
                    path="/property-config/information"
                    element={<Information />}
                  />
                  <Route
                    path="/property-config/room-types"
                    element={<RoomTypes />}
                  />
                  <Route
                    path="/property-config/rate-plans"
                    element={<RatePlans />}
                  />
                  <Route
                    path="/property-config/policies"
                    element={<Policies />}
                  />
                  <Route
                    path="/property-config/taxes-fees"
                    element={<TaxesFees />}
                  />
                </Route>
                <Route path="/contact-us" element={<ContactUs />} />
              </Route>
            </Route>
          </Routes>
        </Router>
        <ToastContainer />
      </AnalyticsHubProvider>
    </AuthProvider>
  );
}

export default App;
